// ============================================================================
// CANCUN100 — resumenAgentePDF.js
// Generador COMPARTIDO del PDF Individual v2 del vendedor (jsPDF por CDN).
// Lo usan: el admin (tab Vendedores → botón 📄 por fila) y el propio agente
// (botón "Mi resumen de la semana", sáb/dom). 100% presentación; recibe datos
// ya calculados (cero matemática aquí).
// ============================================================================

const F0 = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const money = (n) => '$' + F0.format(Number(n || 0));
const trunc = (s, n) => { const t = String(s || '').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };

// Fecha + hora del corte en zona America/Cancun (para la cabecera).
export const corteCancun = () => {
  try {
    return new Date().toLocaleString('es-MX', {
      timeZone: 'America/Cancun', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).replace(',', ' ·');
  } catch (_) { return ymd(new Date()); }
};

// Paleta (RGB para jsPDF)
const ROSA = [247, 71, 128], NARANJA = [255, 140, 66], VERDE = [0, 176, 122], GRIS = [120, 120, 130], OSCURO = [38, 34, 46];

// Carga dinámica de jsPDF + autotable por CDN (idempotente vía window.jspdf).
let pdfLoadingPromise = null;
const loadScript = (src) => new Promise((res, rej) => { const s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
const ensureJsPDF = async () => {
  if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
  if (!pdfLoadingPromise) {
    pdfLoadingPromise = (async () => {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
    })();
  }
  await pdfLoadingPromise;
  return window.jspdf.jsPDF;
};

// Barra de degradado (jsPDF no soporta gradientes → se aproxima con rebanadas).
const gradBar = (doc, x, y, w, h, c1, c2, N = 140) => {
  const sw = w / N;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    doc.setFillColor(
      Math.round(c1[0] + (c2[0] - c1[0]) * t),
      Math.round(c1[1] + (c2[1] - c1[1]) * t),
      Math.round(c1[2] + (c2[2] - c1[2]) * t),
    );
    doc.rect(x + sw * i, y, sw + 0.35, h, 'F');
  }
};

const footerCasa = (doc) => {
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i); doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(150, 150, 150);
    doc.text('Calle Tejon 14, Cancun Q.Roo. Mexico  |  info@cancun100.com  |  cancun100.com by Xpectative Travel', 14, 289);
    doc.text(`Pagina ${i} de ${n}`, 180, 289);
  }
};

// ── Generador principal ──
// data = {
//   nombre, periodoLabel, corte,
//   comisionGenerada, ventaTotal, comisionYaPagada, liberadaPend, bloqueadaHist,
//   folios: [{ folio, cliente, fecha, comision, venta }],
//   balances: [{ fecha, folio, monto, semanaVenta }],
//   frase1, frase2,
// }
export async function generarResumenAgentePDF(data) {
  const JsPDF = await ensureJsPDF();
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  const nombre = data.nombre || 'Vendedor';

  // ── Cabecera v2 (Opción 1): fondo casi blanco + filo de marca + comisión estrella ──
  doc.setFillColor(253, 251, 250); doc.rect(0, 0, W, 42, 'F');
  gradBar(doc, 0, 0, W, 1.8, ROSA, NARANJA); // filo superior ~5pt (rosa→naranja)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRIS);
  doc.text('RESUMEN SEMANAL DE COMISIONES', 14, 12);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...OSCURO);
  doc.text(nombre, 14, 24);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRIS);
  doc.text(data.periodoLabel || '', 14, 31);
  if (data.corte) doc.text(`Corte al ${data.corte}`, 14, 36.5);
  // Bloque TU COMISIÓN (derecha) — cifra en naranja sólido (legible en jsPDF).
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRIS);
  doc.text('TU COMISION', 196, 14, { align: 'right' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...NARANJA);
  doc.text(money(data.comisionGenerada), 196, 25, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...GRIS);
  doc.text(`Venta bruta (ref.): ${money(data.ventaTotal)}`, 196, 31, { align: 'right' });

  // Frase 1
  doc.setFont('helvetica', 'italic'); doc.setFontSize(10); doc.setTextColor(...OSCURO);
  doc.text(doc.splitTextToSize(`"${data.frase1 || ''}"`, W - 28), 14, 51);

  // ── Tu comisión: panorama completo (4 líneas) ──
  let y = 60;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...ROSA);
  doc.text('Tu comision', 14, y); y += 5;
  const linea = (titulo, valor, color, sub) => {
    doc.setFillColor(248, 246, 249); doc.roundedRect(14, y, 182, 16, 2, 2, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...OSCURO); doc.text(titulo, 18, y + 7);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...GRIS); doc.text(sub, 18, y + 12.5);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...color); doc.text(money(valor), 192, y + 10, { align: 'right' });
    y += 19;
  };
  linea('Generada en el periodo', data.comisionGenerada, OSCURO, 'Lo que produjeron tus ventas este periodo');
  linea('Ya cobrada', data.comisionYaPagada, VERDE, 'Este dinero ya te lo pagamos. Bien hecho.');
  linea('Liberada, lista para pagarte', data.liberadaPend, VERDE, 'La casa ya te la puede pagar');
  linea('Bloqueada (historica)', data.bloqueadaHist, NARANJA, 'Se libera en cuanto tus clientes terminen de pagar');
  y += 4;

  // ── Tus folios del periodo (con columna CLIENTE) ──
  if (y > 235) { doc.addPage(); y = 20; }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...ROSA);
  doc.text('Tus folios del periodo', 14, y);
  const bodyV = (data.folios || []).map((f) => [
    f.folio || '-', trunc(f.cliente, 20), String(f.fecha || '').slice(0, 10),
    money(f.comision), money(f.venta),
  ]);
  if (bodyV.length === 0) bodyV.push(['-', '-', '', money(0), money(0)]);
  doc.autoTable({
    startY: y + 3,
    head: [['Folio', 'Cliente', 'Fecha', 'Comision', 'Venta (ref.)']],
    body: bodyV,
    styles: { fontSize: 9, cellPadding: 2, overflow: 'ellipsize' },
    headStyles: { fillColor: OSCURO, textColor: [255, 255, 255] },
    columnStyles: {
      1: { cellWidth: 46 },
      3: { halign: 'right', fontStyle: 'bold', textColor: NARANJA },
      4: { halign: 'right', textColor: GRIS, fontSize: 8 },
    },
    theme: 'striped',
  });
  y = doc.lastAutoTable.finalY + 10;

  // ── Balances de tu semana ──
  if (y > 240) { doc.addPage(); y = 20; }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...ROSA);
  doc.text('Pagos que te llegaron de ventas anteriores', 14, y);
  const bodyB = (data.balances || []).map((r) => [
    String(r.fecha || '').slice(0, 10), r.folio || '-', money(r.monto), String(r.semanaVenta || '').slice(0, 10),
  ]);
  if (bodyB.length === 0) bodyB.push(['-', '-', money(0), '']);
  doc.autoTable({
    startY: y + 3,
    head: [['Fecha', 'Folio', 'Monto', 'Semana de venta']],
    body: bodyB,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: VERDE, textColor: [255, 255, 255] },
    columnStyles: { 2: { halign: 'right' } },
    theme: 'striped',
  });
  y = doc.lastAutoTable.finalY + 12;

  // ── Cierre ──
  if (y > 265) { doc.addPage(); y = 20; }
  doc.setFont('helvetica', 'italic'); doc.setFontSize(10); doc.setTextColor(...ROSA);
  doc.text(doc.splitTextToSize(`"${data.frase2 || ''}"`, W - 28), 14, y);

  footerCasa(doc);
  doc.save(data.filename || `resumen_${nombre.replace(/\s+/g, '_')}_${ymd(new Date())}.pdf`);
}
